/**
 * Procedure Compliance Review (Varo, May 2026 — Dave M.)
 *
 * Lets analysts upload internal procedure / SOP PDFs and run them through
 * the same rules engine that powers marketing review. Surfaces compliance
 * red flags in proposed procedures BEFORE they go to production
 * ("step 3 says collect race" -> fair-lending finding).
 *
 * Reuses the existing Upload + Report pipeline behind the scenes; the
 * upload is tagged with contentCategory='PROCEDURE' so it's filterable
 * separately from marketing reports.
 *
 * Click into a procedure review -> the existing ReportDetailPage renders,
 * showing the same findings UI that marketing uses (severity badges,
 * dismissal flow, audit trail).
 */

import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

import { uploadApi, teamApi, reportApi } from '../lib/api';

export default function ProceduresPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');

  // Teams (reused from upload page pattern)
  const { data: teamsRaw } = useQuery({
    queryKey: ['teams', 'list-unwrapped'],
    queryFn: async () => (await teamApi.getTeams()).data,
  });
  const teams = teamsRaw?.teams || teamsRaw || [];
  const activeTeamId = selectedTeam || (teams.length === 1 ? teams[0]?.id : '') || '';

  // Procedure reports list — scoped to contentCategory=PROCEDURE.
  // Auto-refresh while any are PENDING/PROCESSING so the analyst sees
  // results land without manual refresh.
  const { data: reportsData, refetch } = useQuery({
    queryKey: ['procedure-reports', activeTeamId],
    queryFn: async () => {
      const res = await reportApi.getReports({
        ...(activeTeamId ? { teamId: activeTeamId } : {}),
        // contentCategory is forwarded as an arbitrary param via axios;
        // the `as any` cast below covers the loose typing.
        contentCategory: 'PROCEDURE',
        limit: 25,
      } as any);
      return res.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasPending = data?.reports?.some((r: any) =>
        r.status === 'PENDING' || r.status === 'PROCESSING'
      );
      return hasPending ? 8000 : false;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      // Upload each file individually with contentCategory=PROCEDURE so
      // the queue tags them right. Procedure docs are typically
      // submitted one at a time, but we support batch select for
      // convenience.
      const results: any[] = [];
      for (const file of files) {
        const res = await uploadApi.uploadSingle(
          file,
          activeTeamId || undefined,
          undefined, // no landing-page URL for procedures
          'PROCEDURE',
        );
        results.push(res);
      }
      return results;
    },
    onSuccess: () => {
      toast.success(`${files.length} procedure${files.length === 1 ? '' : 's'} submitted for review`);
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ['procedure-reports'] });
      refetch();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Upload failed');
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 25 * 1024 * 1024, // 25MB — procedure docs are usually small
  });

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const needsTeamSelection = teams.length > 1 && !activeTeamId;

  const handleUpload = () => {
    if (files.length === 0) {
      toast.error('Pick at least one file');
      return;
    }
    if (needsTeamSelection) {
      toast.error('Pick a team first');
      return;
    }
    uploadMutation.mutate();
  };

  const reports: any[] = reportsData?.reports || [];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-8 w-8 text-teal-600" />
            Procedure Review
          </h1>
          <p className="mt-2 text-gray-600">
            Upload internal procedure / SOP documents to surface compliance issues before they go to production.
            Same rules engine that powers ad review — flags fair-lending, UDAAP, Reg E, privacy, and disclosure
            risks in proposed processes.
          </p>
        </div>
      </div>

      {/* Team selector */}
      {teams.length > 0 && (
        <div className="mb-6">
          <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-2">
            Submit to Team
          </label>
          <select
            id="team"
            value={selectedTeam || (teams.length === 1 ? teams[0].id : '')}
            onChange={e => setSelectedTeam(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
          >
            {teams.length > 1 && <option value="">Select a team</option>}
            {teams.map((t: any) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-white hover:border-teal-400'}
        `}
      >
        <input {...getInputProps()} />
        <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        {isDragActive ? (
          <p className="text-teal-700 font-medium">Drop procedure PDFs here…</p>
        ) : (
          <>
            <p className="text-gray-700 font-medium mb-1">Drop a procedure PDF here, or click to browse</p>
            <p className="text-xs text-gray-500">Accepts .pdf, .txt, .docx — up to 25 MB</p>
          </>
        )}
      </div>

      {/* Selected files */}
      {files.length > 0 && (
        <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            {files.length} file{files.length === 1 ? '' : 's'} ready to upload
          </h3>
          <ul className="space-y-1 mb-3">
            {files.map((f, idx) => (
              <li key={idx} className="flex items-center justify-between text-sm py-1">
                <span className="flex items-center gap-2 text-gray-700">
                  <DocumentIcon className="h-4 w-4 text-gray-400" />
                  {f.name} <span className="text-xs text-gray-400">({(f.size / 1024).toFixed(1)} KB)</span>
                </span>
                <button onClick={() => removeFile(idx)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={handleUpload}
            disabled={uploadMutation.isPending}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5"
          >
            {uploadMutation.isPending && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {uploadMutation.isPending ? 'Submitting…' : 'Submit for review'}
          </button>
        </div>
      )}

      {/* Recent procedure reviews */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Recent procedure reviews</h2>
          <button onClick={() => refetch()} className="text-sm text-teal-700 hover:text-teal-900 font-medium">
            Refresh
          </button>
        </div>

        {reports.length === 0 ? (
          <div className="bg-white rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
            <ClipboardDocumentListIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-700 font-semibold">No procedure reviews yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Drop a PDF above to run your first procedure through the rules engine.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {reports.map((r: any) => {
              const violationCount = Array.isArray(r.violations) ? r.violations.length : 0;
              const liveViolations = Array.isArray(r.violations)
                ? (r.violations as any[]).filter(v => !v.dismissed && !v.exemptedByExemption).length
                : 0;

              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={() => navigate(`/reports/${r.id}`)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <Link
                        to={`/reports/${r.id}`}
                        className="text-sm font-medium text-ocean-600 hover:text-ocean-700 truncate block"
                        onClick={e => e.stopPropagation()}
                      >
                        {r.upload?.fileName || 'Untitled'}
                      </Link>
                      <p className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
                        {violationCount > 0 && (
                          <> · {liveViolations}/{violationCount} finding{violationCount === 1 ? '' : 's'} live</>
                        )}
                      </p>
                    </div>
                  </div>
                  <StatusPill status={r.status} />
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-component ──────────────────────────────────────────────────────────

function StatusPill({ status }: { status: string }) {
  switch (status) {
    case 'PENDING':
    case 'PROCESSING':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          <ArrowPathIcon className="h-3 w-3 animate-spin" />
          {status === 'PENDING' ? 'Pending' : 'Processing'}
        </span>
      );
    case 'PASSED':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircleIcon className="h-3 w-3" />
          No issues
        </span>
      );
    case 'FAILED':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
          <ExclamationTriangleIcon className="h-3 w-3" />
          Findings
        </span>
      );
    case 'ERROR':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          <XCircleIcon className="h-3 w-3" />
          Error
        </span>
      );
    default:
      return <span className="text-xs text-gray-500">{status}</span>;
  }
}
